import React, { useState } from 'react';
import { X, Clock } from 'lucide-react';

const CallbackModal = ({ lead, onSave, onClose, saving }) => {
  // Default to tomorrow at 10:00
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const defaultDate = tomorrow.toISOString().split('T')[0];

  const [date, setDate] = useState(defaultDate);
  const [time, setTime] = useState('10:00');
  const [notes, setNotes] = useState('');

  if (!lead) return null;

  const handleSave = () => {
    const callbackDatetime = `${date} ${time}:00`;
    onSave({
      lead_id: lead.id,
      callback_datetime: callbackDatetime,
      callback_notes: notes.trim() || null
    });
  };

  // Quick time shortcuts
  const timeShortcuts = [
    { label: '9am', value: '09:00' },
    { label: '10am', value: '10:00' },
    { label: '11am', value: '11:00' },
    { label: '12pm', value: '12:00' },
    { label: '1pm', value: '13:00' },
    { label: '2pm', value: '14:00' },
    { label: '3pm', value: '15:00' },
    { label: '4pm', value: '16:00' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-gray-900">Schedule Callback</h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X size={20} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Business name */}
          <div className="text-sm text-gray-600">
            <span className="font-semibold">{lead.business_name}</span>
            {lead.phone_number && (
              <span className="ml-2 text-green-600">{lead.phone_number}</span>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full min-h-[48px] rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4"
            />
          </div>

          {/* Time shortcuts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
            <div className="grid grid-cols-4 gap-2 mb-2">
              {timeShortcuts.map(t => (
                <button
                  key={t.value}
                  onClick={() => setTime(t.value)}
                  className={`min-h-[40px] rounded-lg text-sm font-medium transition-all
                    ${time === t.value
                      ? 'bg-blue-100 text-blue-800 ring-2 ring-blue-500'
                      : 'bg-gray-50 text-gray-700 active:bg-gray-100'
                    }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <input
              type="time"
              value={time}
              onChange={e => setTime(e.target.value)}
              className="w-full min-h-[48px] rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base px-4"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Callback reason / what to discuss..."
              rows={2}
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-base p-3"
            />
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saving || !date || !time}
            className="w-full flex items-center justify-center gap-2 min-h-[52px] rounded-xl bg-amber-500 active:bg-amber-700 text-white font-bold text-base disabled:opacity-50 transition-all"
          >
            <Clock size={20} />
            <span>{saving ? 'Scheduling...' : 'Schedule Callback'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallbackModal;
